import { useEffect, useState } from "react";

import { useCurrencyStore } from "@calcom/lib/currencyStore";

type AppPriceMeta = {
  // USD cents from backend
  price?: number;
  // Optional server-side converted cents + the currency it was converted to
  priceConvertedCents?: number;
  currency?: string;
};

export default function Price({ stripeAppData }: { stripeAppData: AppPriceMeta | undefined }) {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => setHasMounted(true), []);

  const { currency, rates } = useCurrencyStore();

  // If we don’t have any price yet, don’t render
  const hasUsdPrice = typeof stripeAppData?.price === "number" && stripeAppData.price > 0;
  const hasServerConverted =
    typeof stripeAppData?.priceConvertedCents === "number" && typeof stripeAppData?.currency === "string";

  if (!hasMounted) return null;
  if (!hasUsdPrice && !hasServerConverted) return null;

  // Always start from USD cents
  const usdCents = Number(stripeAppData?.price ?? 0);

  // Use server-converted price only if it matches the current UI currency
  const serverConvertedCents =
    hasServerConverted && stripeAppData?.currency?.toUpperCase() === currency.toUpperCase()
      ? stripeAppData?.priceConvertedCents
      : undefined;

  // Fallback: client conversion (stay in cents)
  const rate = rates?.[currency] ?? 1;
  const fallbackCents = Math.round(usdCents * rate);

  const centsToShow = typeof serverConvertedCents === "number" ? serverConvertedCents : fallbackCents;

  return (
    <>
      {new Intl.NumberFormat("en", {
        style: "currency",
        currency: currency.toUpperCase(),
      }).format(centsToShow / 100)}
    </>
  );
}
